import type { SolarProposalDesignDto } from "~/api/dto/solar-proposal-design.dto"

import type {
  SolarResidence,
  SolarSpec,
  SolarSystem,
} from "./solar-presentation"
import type { SolarPresentationHeader } from "./solar-presentation-data"

const UNAVAILABLE = `—`

export const formatNumber = (
  value: number,
  maximumFractionDigits: number,
): string => Intl.NumberFormat(`en-US`, { maximumFractionDigits }).format(value)

const withUnit = (
  value: number | null,
  maximumFractionDigits: number,
  unit: string,
): string =>
  value === null
    ? UNAVAILABLE
    : `${formatNumber(value, maximumFractionDigits)}${unit}`

/** `asphalt_shingle` reads as "Asphalt shingle roof". */
const formatRoofType = (roofType: string): string => {
  const words = roofType.replace(/[_-]+/g, ` `).trim().toLowerCase()
  const label = `${words.charAt(0).toUpperCase()}${words.slice(1)}`

  return /\broof$/.test(words) ? label : `${label} roof`
}

/** Equipment names often lead with the manufacturer already; say it once. */
export const equipmentLabel = (
  manufacturer: string | null,
  name: string,
): string =>
  manufacturer &&
  !`${name.toLowerCase()} `.startsWith(`${manufacturer.toLowerCase()} `)
    ? `${manufacturer} ${name}`
    : name

export const toSolarResidence = (
  design: SolarProposalDesignDto,
  proposal: Pick<SolarPresentationHeader, `address` | `homeowner`>,
): SolarResidence => {
  const { utilityName, ratePlan, roofType } = design.site

  return {
    familyName: proposal.homeowner?.lastName.trim() || null,
    address: proposal.address,
    siteDetails: [
      utilityName,
      ratePlan,
      roofType ? formatRoofType(roofType) : null,
    ].filter((detail): detail is string => Boolean(detail)),
    image: design.image,
    layout: design.layout,
  }
}

/** Formatting only: every figure arrives computed from the design read. */
export const toSolarSystem = (design: SolarProposalDesignDto): SolarSystem => {
  const { panels, storage } = design
  const specs: SolarSpec[] = [
    {
      label: `System size`,
      value: withUnit(design.systemSizeKw, 2, ` kW`),
      sub: `Direct-current capacity`,
      icon: `panel`,
    },
    {
      label: `Energy offset`,
      value: withUnit(
        design.offsetPct === null ? null : Math.round(design.offsetPct),
        0,
        `%`,
      ),
      sub:
        design.offsetSource === `aurora`
          ? `of your annual usage, per the Aurora design`
          : `of your annual usage`,
      icon: `sun`,
    },
    {
      label: `Production`,
      value: withUnit(design.annualProductionKwh, 0, ` kWh`),
      sub: `per year`,
      icon: `bolt`,
    },
  ]

  if (storage) {
    const quantity = storage.quantity > 1 ? ` ×${storage.quantity}` : ``
    specs.push({
      label: `Storage`,
      value: withUnit(storage.usableKwh, 2, ` kWh`),
      sub: `${equipmentLabel(storage.manufacturer, storage.name)}${quantity}`,
      icon: `battery`,
    })
  }

  specs.push({
    label: `Number of panels`,
    value: panels?.count == null ? UNAVAILABLE : formatNumber(panels.count, 0),
    sub: panels
      ? equipmentLabel(panels.manufacturer, panels.moduleName)
      : UNAVAILABLE,
    icon: `grid`,
  })

  return { specs, hasStorage: storage !== null }
}
